import { useState } from 'react'
import { nodes as api } from '@/api/endpoints'
import { Err } from '@/components/Err'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { candidateWord, unavailableWord } from '@/domain/cluster'
import type { ClusterCandidate, NodeClusterDecisionRequest } from '@/api/types'

// 노드를 클러스터에 넣는 두 가지 길. 생성은 새 이름으로만 — 이미 있는 이름은 받지 않는다.
// 연결은 이미 있는 클러스터 목록에서 고른다. 응답 없는 클러스터를 고르면 보류와 같은 이름으로 생성 중 하나를 고른다.

export type DecisionMode = 'create' | 'connect'

type Choice = '' | 'hold' | 'replace'

export function ClusterDecisionDialog({
  mode,
  open,
  nodeId,
  candidates,
  onClose,
  onDecided,
}: {
  mode: DecisionMode
  open: boolean
  nodeId: string
  candidates: ClusterCandidate[]
  onClose: () => void
  onDecided: () => void
}) {
  const [text, setText] = useState('')
  const [pickedId, setPickedId] = useState<string | null>(null)
  const [choice, setChoice] = useState<Choice>('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const trimmed = text.trim()
  const taken = mode === 'create' && candidates.some((c) => c.name === trimmed)
  const target = mode === 'connect' ? (candidates.find((c) => c.id === pickedId) ?? null) : null
  const needsChoice = target !== null && target.state === 'active' && !target.live

  const reset = () => {
    setText('')
    setPickedId(null)
    setChoice('')
    setError(null)
  }
  const close = () => {
    if (busy) return
    reset()
    onClose()
  }

  const submitLabel = mode === 'create' ? '생성' : needsChoice ? (choice === 'replace' ? '같은 이름으로 생성' : '보류') : '연결'
  const disabled =
    busy || (mode === 'create' ? trimmed === '' || taken : target === null || (needsChoice && !choice))

  const submit = async () => {
    if (disabled) return
    const body: NodeClusterDecisionRequest =
      mode === 'create'
        ? { name: trimmed }
        : { cluster_id: target!.id, ...(needsChoice && choice ? { on_unavailable: choice } : {}) }
    setBusy(true)
    setError(null)
    try {
      await api.decideCluster(nodeId, body)
      // 결과는 소속 상태가 말한다(생성 중·연결 중·변경 대기). 따로 알리지 않는다.
      reset()
      onDecided()
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? close() : undefined)}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? '클러스터 생성' : '클러스터 연결'}</DialogTitle>
        </DialogHeader>
        {mode === 'create' ? (
          <div className="grid gap-2">
            <Label htmlFor="cluster-name">이름</Label>
            <Input
              id="cluster-name"
              autoComplete="off"
              maxLength={63}
              aria-invalid={taken || undefined}
              value={text}
              onChange={(event) => setText(event.target.value)}
            />
            {taken ? <div className="text-[12px] text-danger-ink">이미 있는 이름입니다.</div> : null}
          </div>
        ) : (
          <ul role="listbox" aria-label="클러스터" className="max-h-56 overflow-y-auto rounded-md border">
            {candidates.length === 0 ? (
              <li className="px-3 py-2 text-[12px] text-muted-foreground">연결할 클러스터 없음</li>
            ) : (
              candidates.map((c) => (
                <li
                  key={c.id}
                  role="option"
                  aria-selected={c.id === pickedId}
                  tabIndex={0}
                  className={`flex cursor-pointer items-center justify-between px-3 py-2 text-sm hover:bg-surface-2 ${c.id === pickedId ? 'bg-surface-2' : ''}`}
                  onClick={() => {
                    setPickedId(c.id)
                    setChoice('')
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      setPickedId(c.id)
                      setChoice('')
                    }
                  }}
                >
                  <span className="truncate">{c.name}</span>
                  <span className="ml-3 shrink-0 text-[12px] text-muted-foreground">{candidateWord(c)}</span>
                </li>
              ))
            )}
          </ul>
        )}
        {needsChoice ? (
          <fieldset className="grid gap-1.5 rounded-md border px-3 py-2">
            <legend className="px-1 text-[12px] font-medium">응답 없음</legend>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" name="unavailable" value="hold" checked={choice === 'hold'} onChange={() => setChoice('hold')} />
              보류
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" name="unavailable" value="replace" checked={choice === 'replace'} onChange={() => setChoice('replace')} />
              기록을 지우고 생성
            </label>
          </fieldset>
        ) : target && !target.joinable ? (
          <div className="text-[12px] text-muted-foreground">{unavailableWord(target)}</div>
        ) : null}
        <Err error={error} />
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={close}>취소</Button>
          <Button disabled={disabled} onClick={() => void submit()}>
            {busy ? '요청 중' : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
